import React, { useState, useEffect, useMemo } from 'react'
import { inject } from 'mobx-react'
import { observer } from 'mobx-react-lite'
import Helmet from 'react-helmet'
import AppHeader from '../../../components/AppView/Header'
import { CurrentCompanyStore } from '../../../stores/CurrentCompanyStore'
import { AppMainView } from '../../../components/AppView'
import { parseErrorMsg } from '../../../utilities/helpers'
import * as ocs from '@getgreenline/ocs'
import { BlazeButton } from '../../../components/CustomButtons/BlazeButton'
import Endpoints from '../../../constants/Endpoints'
import { CurrentUserStore } from '../../../stores/CurrentUserStore'
import { isEmpty } from 'lodash'
import { dayjs } from '../../../utilities/dayjs'
import { Routes } from '../../../constants/Routes'
import { Alert, Table, message } from 'antd-v4'
import { ColumnsType } from 'antd-v4/lib/table'
import { usePaginatedQuery } from '../../../utilities/hooks/usePaginatedQuery'
import { Link } from 'react-router'
import { ICompany } from '@getgreenline/homi-shared'
import { ProductV2Api } from '@getgreenline/products'

interface InjectedProps {
  currentUserStore: CurrentUserStore
  currentCompanyStore: CurrentCompanyStore
}

interface IComplianceIssue {
  productId: string
  productName?: string
  productSku?: string
  productBarcode?: string
  productCategory?: string
  issueCode: string
  createDate: string
}

type TTableColumns = ColumnsType<IComplianceIssue>

const ComplianceIssuesComponent = ({ currentCompanyStore, currentUserStore }: InjectedProps) => {
  const [isLoadingTable, setIsLoadingTable] = useState<boolean>(false)
  const [isSubmittingResync, setIsSubmittingResync] = useState<boolean>(false)
  const [unresolvedProducts, setUnresolvedProducts] = useState<Map<string, Set<string>>>(new Map())
  const [pagesResynced, setPagesResynced] = useState<number[]>([])
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [complianceIssues, setComplianceIssues] = useState<
    ocs.IGetComplianceIssuesResponseContract['items']
  >([])

  const pageSize = 6
  const pagesToFetch = 5

  const company: ICompany | undefined = currentCompanyStore.company
  const hashKeys: ocs.ICreateHashKeyRequestContract[] | undefined =
    currentCompanyStore.globalHashKeys

  if (!company) return null

  const isResyncDisabled =
    pagesResynced.includes(currentPage) || isEmpty(hashKeys) || isEmpty(complianceIssues)

  const issueCodeMap = {
    MISSING_BARCODE: 'Missing Barcode',
    MISSING_SKU: 'Missing SKU',
  }

  const columns: TTableColumns = [
    {
      title: 'Product Name',
      key: 'productName',
      width: '22.5%',
      render: (complianceIssue: IComplianceIssue) => (
        <Link to={Routes.EDIT_PRODUCT(company.id, complianceIssue.productId)} target='_blank'>
          {complianceIssue.productName}
        </Link>
      ),
    },
    {
      title: 'Issue',
      dataIndex: 'issueCode',
      key: 'issueCode',
      width: '12.5%',
    },
    {
      title: 'SKU',
      dataIndex: 'productSku',
      key: 'productSku',
      width: '17.5%',
    },
    {
      title: 'Barcode',
      dataIndex: 'productBarcode',
      key: 'productBarcode',
      width: '17.5%',
    },
    {
      title: 'Category',
      dataIndex: 'productCategory',
      key: 'productCategory',
      width: '15%',
    },
    {
      title: 'Issue Date',
      dataIndex: 'createDate',
      key: 'createDate',
      width: '15%',
    },
  ]

  const getComplianceIssuesPaginated = usePaginatedQuery(({ exclusiveStartKeySk }) => {
    const tenantPathParams: ocs.ITenantPathParams = {
      gateway: Endpoints.AUTHZ_GATEWAY_URL,
      tenantId: `${company.id}`,
    }

    const paginationFilters = { limit: pageSize * pagesToFetch, exclusiveStartKeySk }

    return ocs.getComplianceIssues(tenantPathParams, paginationFilters)
  })

  const fetchComplianceIssues = async () => {
    setIsLoadingTable(true)

    try {
      const response = await getComplianceIssuesPaginated({
        currentPage,
        pageSize,
        shouldForceRefresh: false,
      })

      if (!response) return

      setComplianceIssues([...complianceIssues, ...response.items])
    } catch (error) {
      console.error(error)
      message.error(parseErrorMsg(error) || 'An error ocurred while fetching compliance issues')
    } finally {
      setIsLoadingTable(false)
    }
  }

  const resync = async () => {
    setIsSubmittingResync(true)

    try {
      if (isEmpty(hashKeys)) {
        throw new Error('No hash keys associated with the current company')
      }

      const currentUserID: string | undefined = currentUserStore?.currentUser?.id

      const payloadStart = (currentPage - 1) * pageSize
      const payloadEnd = payloadStart + pageSize

      const pageComplianceIssues = complianceIssues.slice(payloadStart, payloadEnd)
      const productIds = pageComplianceIssues.map((issue) => issue.entityId)

      const productsToCheck = await ProductV2Api.getProducts(company.id, {
        updateDate: null,
        productIds: [...new Set(productIds)],
      })

      const unresolvedProductIssues = new Map(
        productsToCheck
          .map((product) => {
            const productIssues = new Set()
            if (!product.sku) productIssues.add(issueCodeMap[ocs.ComplianceIssueCode.MISSING_SKU])
            if (!product.barcode)
              productIssues.add(issueCodeMap[ocs.ComplianceIssueCode.MISSING_BARCODE])
            return [product.id, productIssues] as [string, Set<string>]
          })
          .filter(([_, productIssues]) => productIssues.size > 0),
      )

      if (unresolvedProductIssues.size > 0) {
        setUnresolvedProducts(unresolvedProductIssues)
        message.error('Cannot resync as not all compliance issues are resolved.')
        return
      }

      const payload: ocs.ICreateResyncRequestContract[] = pageComplianceIssues.map((issue) => {
        const rangeStart = new Date(issue.createDate)
        rangeStart.setHours(rangeStart.getHours() - 1)

        return {
          productId: issue.entityId,
          rangeStart: rangeStart.toISOString(),
          rangeEnd: new Date().toISOString(),
          updateBy: currentUserID ? `userId-${currentUserID}` : '',
        }
      })

      for (const hashKey in hashKeys) {
        const tenantHashKeyPathParams: ocs.ITenantHashKeyPathParams = {
          gateway: Endpoints.AUTHZ_GATEWAY_URL,
          tenantId: `${company.id}`,
          hashKey: hashKey,
        }

        await ocs.createResyncProducts(tenantHashKeyPathParams, payload)
      }

      message.success('Resync process has successfully started')
      setPagesResynced((prev) => [...prev, currentPage])
      setUnresolvedProducts(new Map())
    } catch (error) {
      console.error(error)
      message.error('Failed to resync. Please try again or contact support for assistance.')
    } finally {
      setIsSubmittingResync(false)
    }
  }

  const dataSource: IComplianceIssue[] = useMemo(() => {
    return complianceIssues.map((item) => ({
      productId: item.entityId,
      productName: item.metaData?.name,
      productSku: item.metaData?.sku,
      productBarcode: item.metaData?.barcode,
      productCategory: item.metaData?.category,
      issueCode: issueCodeMap[item.issueCode] || item.issueCode,
      createDate: dayjs(item.createDate).format('YYYY-MM-DD hh:mm a'),
    }))
  }, [complianceIssues])

  useEffect(() => {
    fetchComplianceIssues()
  }, [currentPage])

  return (
    <div className='d-flex flex-column h-100'>
      <Helmet>
        <title>Compliance | Compliance Issues</title>
        <meta name='page-loaded' content='true' />
      </Helmet>
      <AppHeader title='Compliance Issues' />
      <AppMainView className='background-grey intercom-pb-2'>
        <Alert
          showIcon
          type={pagesResynced.includes(currentPage) ? 'warning' : 'info'}
          style={{ marginBottom: 16 }}
          message={
            pagesResynced.includes(currentPage)
              ? 'The resync process is in progress. It can take up to 10 minutes to complete. A refresh is needed to see changes applied to the table.'
              : "Click the 'Resync' button below after updating your products."
          }
        />
        <Table
          loading={isLoadingTable}
          dataSource={dataSource}
          columns={columns}
          onChange={({ current }) => setCurrentPage(current || 1)}
          rowClassName={(issue) =>
            unresolvedProducts.get(issue.productId)?.has(issue.issueCode)
              ? 'table-row-background-error'
              : 'background-white'
          }
          rowKey={(issue) => `${issue.productId}:${issue.issueCode}`}
          pagination={{
            showSizeChanger: false,
            position: ['bottomRight'],
            pageSize,
          }}
        />
        <div style={{ paddingTop: '1em', textAlign: 'center' }}>
          <BlazeButton
            buttonProps={{
              size: 'large',
              type: 'primary',
              htmlType: 'submit',
              onClick: resync,
              loading: isSubmittingResync,
              disabled: isResyncDisabled,
            }}
            titleProps={{
              title: isSubmittingResync ? '' : 'Resync',
            }}
          />
        </div>
      </AppMainView>
    </div>
  )
}

export const ComplianceIssues = inject(
  'currentUserStore',
  'currentCompanyStore',
)(observer(ComplianceIssuesComponent))
